import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import type { Deck, Note, Review } from '../shared/types.ts';

// Everything the app needs lives here, so it works with no network at all.
// `dirty` marks rows written on this device that the server hasn't
// acknowledged; it is a number because IndexedDB cannot index booleans.

export type LocalDeck = Deck & { dirty: 0 | 1 };
export type LocalNote = Note & { dirty: 0 | 1 };
export type LocalReview = Review & { dirty: 0 | 1 };

export interface AudioRow {
  hash: string;
  blob: Blob;
  mime: string;
  /** 0 for a recording made here that hasn't reached the server yet. */
  uploaded: 0 | 1;
}

interface Schema extends DBSchema {
  decks: { key: string; value: LocalDeck; indexes: { dirty: number } };
  notes: { key: string; value: LocalNote; indexes: { dirty: number } };
  reviews: { key: string; value: LocalReview; indexes: { dirty: number; cardId: string } };
  audio: { key: string; value: AudioRow; indexes: { uploaded: number } };
  meta: { key: string; value: unknown };
}

export type DB = IDBPDatabase<Schema>;

const NAME = 'flashcards';

export function openDatabase(): Promise<DB> {
  return openDB<Schema>(NAME, 1, {
    upgrade(db) {
      db.createObjectStore('decks', { keyPath: 'id' }).createIndex('dirty', 'dirty');
      const notes = db.createObjectStore('notes', { keyPath: 'id' });
      notes.createIndex('dirty', 'dirty');
      const reviews = db.createObjectStore('reviews', { keyPath: 'id' });
      reviews.createIndex('dirty', 'dirty');
      reviews.createIndex('cardId', 'cardId');
      const audio = db.createObjectStore('audio', { keyPath: 'hash' });
      audio.createIndex('uploaded', 'uploaded');
      db.createObjectStore('meta');
    },
  });
}

export async function getMeta<T>(db: DB, key: string, fallback: T): Promise<T> {
  const v = await db.get('meta', key);
  return v === undefined ? fallback : (v as T);
}

export async function setMeta(db: DB, key: string, value: unknown): Promise<void> {
  await db.put('meta', value, key);
}

export async function wipeDatabase(db: DB): Promise<void> {
  db.close();
  await new Promise<void>((resolve, reject) => {
    const req = indexedDB.deleteDatabase(NAME);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
    req.onblocked = () => resolve();
  });
}
